<template>
  <v-container fluid>
    <PageHeaderSlot>
      <template slot="top-right-slot" v-if="canCreate">
        <FormButton text="createLesson" isAdd class="ml-2" :to="{ name: 'CreateClass' }" />
      </template>
    </PageHeaderSlot>

    <v-card class="custom-calendar custom-table">
      <v-toolbar flat class="custom-table-toolbar" height="auto">
        <v-toolbar-items class="toolbar-left-items flex-grow-1">
          <FormDatePicker
            :dateValue.sync="filter.date"
            hideDetails
            customInputClass="mr-3 mt-2 mb-3 search-input-field"
            placeholder="table.searchDate"
            :readonly="tableLoading"
            @changed="onSearchDateChange($event)"
          />

          <FormSelect
            :fieldValue.sync="filter.course"
            hideDetails
            class="mr-3 mt-2 mb-3 search-select-field"
            placeholder="course"
            :options="courseOptions"
            @changed="search()"
            :readonly="tableLoading"
            enableClear
            isMultiple
            enableTooltip
          />

          <FormSelect
            v-if="!(gTutorViewOwnLessonOnly && userType === 'tutor')"
            :fieldValue.sync="filter.tutor"
            hideDetails
            class="mr-3 mt-2 mb-3 search-select-field"
            placeholder="courseTutor"
            :options="tutorOptions"
            @changed="search()"
            :readonly="tableLoading"
            enableClear
            isMultiple
            enableTooltip
          />
        </v-toolbar-items>

        <v-spacer></v-spacer>

        <v-toolbar-items class="toolbar-right-items">
          <v-btn text small color="error" @click="clearSearch()" :class="{ 'pointer-loading': tableLoading }">
            <v-icon class="mr-1 fi fi-rr-cross-small" color="error" size="18"></v-icon>
            {{ $t('table.clearSearch') }}
          </v-btn>

          <v-btn text small color="primary" @click="refreshData()" :class="{ 'pointer-loading': tableLoading }">
            <v-icon class="mr-1" color="primary" size="18"> {{ icon.mdiRefresh }} </v-icon>
            {{ $t('table.reloadTable') }}
          </v-btn>

          <v-btn
            text
            small
            color="#215003"
            target="_blank"
            rel="noreferrer noopenner"
            @click="exportData()"
            :class="{ 'pointer-loading': tableLoading }"
          >
            <v-progress-circular v-if="exportLoading" class="mr-1" indeterminate color="#215003" :size="15" width="3" />
            <v-icon class="mr-1" v-else color="#215003" size="18"> {{ icon.mdiFileTableOutline }} </v-icon>
            {{ $t('table.exportReport') }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <template v-if="isAppMode(['T37'])">
        <CustomTimetableWeek
          ref="timetable"
          :loading="tableLoading"
          :timetableData="timetableData"
          @dates-update="getTimetable($event)"
          @type-update="filter.calendarType = $event"
          :holidayData="holidayList"
          @event-clicked="navLessonDetail($event)"
          noDataText="calendar.noLesson"
          enableExpand
        >
          <template v-slot:[`event`]="{ event, expand }">
            <TimetableEventCard :eventData="event" :expandStudent="expand" />
          </template>
          <template slot="toolbar-right">
            <v-switch
              v-model="filter.allCenters"
              inset
              :label="$t('expandAllCenters')"
              color="accent"
              dark
              hide-details
              dense
              class="my-4 my-md-0 mr-0 mr-md-4"
              @change="search()"
              :readonly="tableLoading"
            />
          </template>
        </CustomTimetableWeek>
      </template>
      <template v-if="!isAppMode(['T37'])">
        <CustomTimetable
          ref="timetable"
          :loading="tableLoading"
          :timetableData="timetableData"
          @dates-update="getTimetable($event)"
          @type-update="filter.calendarType = $event"
          :holidayData="holidayList"
          @event-clicked="navLessonDetail($event)"
          noDataText="calendar.noLesson"
          enableExpand
        >
          <template v-slot:[`event`]="{ event, expand }">
            <TimetableEventCard :eventData="event" :expandStudent="expand" />
          </template>
          <template slot="toolbar-right">
            <v-switch
              v-model="filter.allCenters"
              inset
              :label="$t('expandAllCenters')"
              color="accent"
              dark
              hide-details
              dense
              class="my-4 my-md-0 mr-0 mr-md-4"
              @change="search()"
              :readonly="tableLoading"
            />
          </template>
        </CustomTimetable>
      </template>
    </v-card>
  </v-container>
</template>

<script>
import { mdiRefresh, mdiFileTableOutline } from '@mdi/js'
import FormDatePicker from '@/components/formField/FormDatePicker.vue'
import FormSelect from '@/components/formField/FormSelect.vue'
import CustomTimetable from '@/components/CustomTimetable.vue'
import CustomTimetableWeek from '@/components/CustomTimetableWeekCustom.vue'
import ExcelMixin from '@/mixins/ExcelMixin.vue'
import TimetableEventCard from '@/components/layout/TimetableEventCard.vue'

export default {
  name: 'Timetable',
  mixins: [ExcelMixin],
  components: {
    FormDatePicker,
    FormSelect,
    CustomTimetable,
    CustomTimetableWeek,
    TimetableEventCard,
  },
  computed: {
    defaultCalendarType() {
      if (this.isAppMode(['DEV', 'UAT', 'T02', 'T40', 'T53', '61+'])) {
        return 'month'
      } else if (this.isAppMode(['T45', 'T51'])) {
        return 'week'
      }
      return 'day'
    },
    gradeOptions() {
      return this.$store.getters.gradeOptions
    },
    // permission & setting
    canCreate() {
      return this.$validate.allowAction('CreateClass', this.userType)
    },
  },
  data: vm => ({
    userType: vm.gfGetUserType(),
    filter: {
      date: '',
      course: [],
      tutor: [],
      dateList: [],
      calendarType: '',
      allCenters: false,
    },
    disableCourseGrade: false,

    courseOptions: [],
    tutorOptions: [],
    holidayList: [],

    tableLoading: false,
    timetableData: [],

    exportLoading: false,

    reloadClick: false,

    // - static
    icon: {
      mdiRefresh,
      mdiFileTableOutline,
    },
  }),
  methods: {
    async _getPayload() {
      let payload = {
        join_tutor_data: true,
        join_student_data: true,
        local_sort: true,
      }

      if (!this.filter.allCenters) {
        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) payload['center_id'] = selectedSchool
      }

      const filterItems = []
      if (this.$validate.DataValid(this.filter.dateList)) {
        if (
          this.filter.dateList.length > 1 &&
          this.filter.dateList[0] !== this.filter.dateList[this.filter.dateList.length - 1]
        ) {
          filterItems.push({ key: 'start_date', value: this.filter.dateList[0] })
          filterItems.push({ key: 'end_date', value: this.filter.dateList[this.filter.dateList.length - 1] })
        } else {
          filterItems.push({ key: 'date', value: this.filter.dateList[0] })
        }
      }

      if (this.$validate.DataValid(this.filter.course) && this.filter.course.length > 0) {
        filterItems.push({ key: 'course_id_list', value: this.filter.course })
      }

      // 如果是tutor account，只可攞自己的lesson
      if (this.gTutorViewOwnLessonOnly && this.userType === 'tutor') {
        const getUser = this.gfGetCurrentUserData()
        filterItems.push({ key: 'staff_id_list', value: [getUser.id] })
      } else if (this.$validate.DataValid(this.filter.tutor, true)) {
        filterItems.push({ key: 'staff_id_list', value: this.filter.tutor })
      }

      if (this.$validate.DataValid(filterItems)) {
        payload['filter_item'] = filterItems
      }

      return payload
    },
    async getTimetable(dateList, regenUrl = true) {
      if (this.$validate.DataValid(dateList)) {
        this.filter.dateList = dateList
      }

      if (regenUrl === true) this._regenerateURL()

      this.$XHR.abort('get_package_sort')
      this.tableLoading = true

      let payload = await this._getPayload()
      if (this.isAppMode(['DEV', 'T02'])) {
        payload['join_student_last_lesson'] = true
        payload['get_from'] = ''
      }
      // ================ compare with cache data ================
      const getUser = this.gfGetCurrentUserData()

      const currentCalendarType = this.filter.calendarType || this.defaultCalendarType
      const cacheFilter = { ...this.$store.getters.timetableCache }
      let newRequest = true
      // -- check self level
      if (
        cacheFilter &&
        getUser &&
        getUser.id === cacheFilter.user_id &&
        cacheFilter.mode === this.$appMode &&
        cacheFilter[currentCalendarType] && !this.reloadClick
      ) {
        const cache = cacheFilter[currentCalendarType]
        if (cache.expired) {
          const now = new Date().getTime()
          if (cache.expired > now) {
            let sameRequest = this._checkSameRequest(cache.payload, payload)
            if (sameRequest) {
              // check `date`
              const foundDateInCache = (cache.payload['filter_item'] || []).find(el => el.key === 'date')
              const foundDateInNew = (payload['filter_item'] || []).find(el => el.key === 'date')
              if (foundDateInCache && foundDateInNew) {
                if (foundDateInCache.value !== foundDateInNew.value) {
                  sameRequest = false
                }
              } else if (foundDateInCache || foundDateInNew) {
                sameRequest = false
              }

              // check `start_date`
              const foundStartDateInCache = (cache.payload['filter_item'] || []).find(el => el.key === 'start_date')
              const foundStartDateInNew = (payload['filter_item'] || []).find(el => el.key === 'start_date')
              if (foundStartDateInCache && foundStartDateInNew) {
                if (foundStartDateInCache.value !== foundStartDateInNew.value) {
                  sameRequest = false
                }
              } else if (foundStartDateInCache || foundStartDateInNew) {
                sameRequest = false
              }

              // check `end_date`
              const foundEndDateInCache = (cache.payload['filter_item'] || []).find(el => el.key === 'end_date')
              const foundEndDateInNew = (payload['filter_item'] || []).find(el => el.key === 'end_date')
              if (foundEndDateInCache && foundEndDateInNew) {
                if (foundEndDateInCache.value !== foundEndDateInNew.value) {
                  sameRequest = false
                }
              } else if (foundEndDateInCache || foundEndDateInNew) {
                sameRequest = false
              }
            }
            if (sameRequest) newRequest = cacheFilter[currentCalendarType]['data']
          }
        }
      }

      // -- check upper level
      if (newRequest === true && !this.reloadClick) {
        if (currentCalendarType === 'week') {
          // find in month
          if (cacheFilter.month.expired) {
            const now = new Date().getTime()
            if (cacheFilter.month.expired > now) {
              const foundStartDateInWeek = (payload['filter_item'] || []).find(el => el.key === 'start_date')
              const foundStartDateInMonth = (cacheFilter.month.payload['filter_item'] || []).find(
                el => el.key === 'start_date',
              )
              const foundEndDateInWeek = (payload['filter_item'] || []).find(el => el.key === 'end_date')
              const foundEndDateInMonth = (cacheFilter.month.payload['filter_item'] || []).find(
                el => el.key === 'end_date',
              )
              if (foundStartDateInWeek && foundStartDateInMonth && foundEndDateInWeek && foundEndDateInMonth) {
                const startDateWeek = new Date(foundStartDateInWeek.value)
                const startDateMonth = new Date(foundStartDateInMonth.value)
                const endDateWeek = new Date(foundEndDateInWeek.value)
                const endDateMonth = new Date(foundEndDateInMonth.value)

                // if start(week) >= start(month) && end(week) <= end(month)
                if (
                  (this.$validate.isDateEqual(startDateWeek, startDateMonth) ||
                    this.$validate.isDateGreater(startDateWeek, startDateMonth)) &&
                  (this.$validate.isDateEqual(endDateWeek, endDateMonth) ||
                    this.$validate.isDateLess(endDateWeek, endDateMonth))
                ) {
                  const sameRequest = this._checkSameRequest(payload, cacheFilter.month.payload)
                  if (sameRequest) {
                    newRequest = []
                    let startDate = this.$formatter.convertDate(foundStartDateInWeek.value)
                    const endDate = this.$formatter.convertDate(foundEndDateInWeek.value)
                    while (
                      this.$validate.isDateLess(startDate, endDate) ||
                      this.$validate.isDateEqual(startDate, endDate)
                    ) {
                      const l = cacheFilter.month.data.filter(el => {
                        const d = this.$formatter.convertDate(el.date)
                        return this.$validate.isDateEqual(startDate, d)
                      })
                      newRequest = newRequest.concat(l)

                      startDate.setDate(startDate.getDate() + 1)
                    }
                  }
                }
              }
            }
          }
        } else if (currentCalendarType === 'day') {
          // find in month
          const getInMonths = this._getDayLessonsFromUpper('month', payload)
          if (getInMonths) {
            newRequest = getInMonths
          }

          // find in week
          if (newRequest === true) {
            const getInWeeks = this._getDayLessonsFromUpper('week', payload)
            if (getInWeeks) {
              newRequest = getInWeeks
            }
          }
        }
      }
      // ================ End of compare with cache data ================

      console.log("this.reloadClick")
      console.log(this.reloadClick)

      if (this.reloadClick){
        newRequest = true
        this.reloadClick = false
      }

      // check loaded
      if (this.isAppMode(['UAT']) || !this.isAppMode(['T01'])) {
        try {
          const { datetime } = await this.$Fetcher.GetCacheTime()
          console.log("check_data")
          const store_time = new Date(datetime).getTime();
          console.log(store_time)
          console.log("cacheFilter[currentCalendarType]")
          console.log(cacheFilter[currentCalendarType].loaded)
          if(store_time > cacheFilter[currentCalendarType].loaded){
            newRequest = true
          }
        } catch (err) {
          // this.$common.error(err)
          // this.timetableData = []
          // this.tableLoading = false
          // if (err === 'abort') {
          //   return false
          // }
        }
      }
      
      console.log("newRequest")
      console.log(newRequest)

      try {
        if (newRequest === true || !Array.isArray(newRequest)) {
          const { data } = await this.$Fetcher.GetLessons(payload)
          this.timetableData = data.map(el => this._parseTimetableData(el))
        } else {
          const newTimetable = [...newRequest]
          if (this.$validate.DataValid(cacheFilter.updateLessons, true)) {
            const updateLessonIds = [...cacheFilter.updateLessons]
            const relatedLessonIds = updateLessonIds.filter(id => {
              const found = newTimetable.find(el => el.id === id)
              return found
            })
            const getNewLessons = await this.$Fetcher.GetLessonsByIdList(relatedLessonIds, {
              join_tutor_data: payload.join_tutor_data || false,
              join_student_data: payload.join_student_data || false,
              join_student_last_lesson: payload.join_student_last_lesson || false,
            })

            for (let x = updateLessonIds.length - 1; x >= 0; x--) {
              for (let i = newTimetable.length - 1; i >= 0; i--) {
                if (updateLessonIds[x] === newTimetable[i].id) {
                  const foundLesson = getNewLessons.find(el => el.id === newTimetable[i].id)
                  if (foundLesson) {
                    newTimetable[i] = this._parseTimetableData(foundLesson)
                  } else {
                    // lesson inactive
                    newTimetable.splice(i, 1)
                  }

                  updateLessonIds.splice(x, 1)
                  break
                }
              }
            }

            this.$store.dispatch('setTimetableCache', {
              updateLessons: updateLessonIds,
              refreshExpired: false,
            })
          }
          this.timetableData = newTimetable
        }

        // staff只可以攞所屬分校lesson
        const getUserSchools = this.gfGetUserSchools()
        this.timetableData = this.timetableData.filter(
          el => !this.$validate.DataValid(el.school_id) || getUserSchools.includes(el.school_id),
        )

        if (this.isAppMode(['T40'])) {
          await this._getLessonCourseCategory()
        }

        this.$store.dispatch('setTimetableCache', {
          user_id: getUser.id,
          mode: this.$appMode,
          key: currentCalendarType,
          payload: payload,
          data: [...this.timetableData],
          refreshExpired: newRequest === true || !Array.isArray(newRequest),
          updateLessons: null,
          refreshLoaded: newRequest === true || !Array.isArray(newRequest),
        })

        this.tableLoading = false
        this.$XHR.restart('get_package_sort')
        this._getPackageSort(this.timetableData.map(el => el.id))
      } catch (err) {
        this.$common.error(err)
        this.timetableData = []
        this.tableLoading = false
        if (err === 'abort') {
          return false
        }
      }

      return true
    },
    _parseTimetableData(el) {
      const studentData = []
      for (let i = 0; i < el.attendance_list.length; i++) {
        const student = el.attendance_list[i]
        if (
          this.$validate.DataValid(student) &&
          !student.removed_student &&
          (student.status === 'pending' ||
            student.status === 'attend' ||
            student.status === 'absent' ||
            student.status === 'sick_leave' ||
            student.status === 'await')
        ) {
          studentData.push({
            id: student.student_id,
            gender: student.gender,
            name:
              student.student_name +
              (this.isAppMode(['T46']) && student.student_nickname ? ` ${student.student_nickname}` : ''),
            student_code: student.student_code,
            grade: student.grade,
            attendance: student.status,
            unpaid: !el.paid_student_ids.includes(student.student_id),
            last_package_lesson: student.last_lesson_id === el.id,
            trial: student.trial,
            new_student: false, // TODO: #234
            is_skip_to_lesson: student.is_skip_to_lesson,
            package_quota_count: student.package_quota_count,
            birthday: student.birthday,
          })
        }
      }

      return {
        id: el.id,
        date: el.date,
        start_time: el.start_time,
        end_time: el.end_time,
        remark: el.remark,
        course_name: el.course_name,
        color: '#eeeeee',
        colorCode: el.color,
        max_student: el.max_student,
        grade: this.disableCourseGrade ? '' : el.grade,
        student_data: studentData,
        current_student_num: el.current_student_num || 0,
        classroom: this.$validate.DataValid(el.classroom_data) ? el.classroom_data.name : '',
        tutor: el.tutor_data_list.map(t => t.name).join(', '),
        school_id: el.school_id,
        course_category_id: el.course_category_id,
      }
    },
    _checkSameRequest(payload1, payload2) {
      let sameRequest = true
      // check `center_id`
      if (this.$validate.DataValid(payload1['center_id']) && this.$validate.DataValid(payload2['center_id'])) {
        if (payload1['center_id'] !== payload2['center_id']) {
          sameRequest = false
        }
      } else if (this.$validate.DataValid(payload1['center_id']) || this.$validate.DataValid(payload2['center_id'])) {
        sameRequest = false
      }

      // check `course_id_list`
      const foundCourseInCache = (payload1['filter_item'] || []).find(el => el.key === 'course_id_list')
      const foundCourseInNew = (payload2['filter_item'] || []).find(el => el.key === 'course_id_list')
      if (foundCourseInCache && foundCourseInNew) {
        if (foundCourseInCache.value.length !== foundCourseInNew.value.length) {
          sameRequest = false
        } else {
          for (let x = 0; x < foundCourseInNew.value.length; x++) {
            if (!foundCourseInCache.value.includes(foundCourseInNew.value[x])) {
              sameRequest = false
              break
            }
          }
        }
      } else if (foundCourseInCache || foundCourseInNew) {
        sameRequest = false
      }

      // check `staff_id_list`
      const foundStaffInCache = (payload1['filter_item'] || []).find(el => el.key === 'staff_id_list')
      const foundStaffInNew = (payload2['filter_item'] || []).find(el => el.key === 'staff_id_list')
      if (foundStaffInCache && foundStaffInNew) {
        if (foundStaffInCache.value.length !== foundStaffInNew.value.length) {
          sameRequest = false
        } else {
          for (let x = 0; x < foundStaffInNew.value.length; x++) {
            if (!foundStaffInCache.value.includes(foundStaffInNew.value[x])) {
              sameRequest = false
              break
            }
          }
        }
      } else if (foundStaffInCache || foundStaffInNew) {
        sameRequest = false
      }

      return sameRequest
    },
    _getDayLessonsFromUpper(upper, payload) {
      let list = null
      const cacheFilter = this.$store.getters.timetableCache
      if (cacheFilter && cacheFilter[upper] && cacheFilter[upper].expired) {
        const now = new Date().getTime()
        if (cacheFilter[upper].expired > now) {
          const foundStartDateInUpper = (cacheFilter[upper].payload['filter_item'] || []).find(
            el => el.key === 'start_date',
          )
          const foundEndDateInUpper = (cacheFilter[upper].payload['filter_item'] || []).find(
            el => el.key === 'end_date',
          )
          const foundDateInDay = (payload['filter_item'] || []).find(el => el.key === 'date')
          if (foundStartDateInUpper && foundEndDateInUpper && foundDateInDay) {
            const startDateUpper = new Date(foundStartDateInUpper.value)
            const endDateUpper = new Date(foundEndDateInUpper.value)
            const dateInDay = new Date(foundDateInDay.value)

            // if date(day) >= start(upper) && date(day) <= end(upper)
            if (
              (this.$validate.isDateEqual(dateInDay, startDateUpper) ||
                this.$validate.isDateGreater(dateInDay, startDateUpper)) &&
              (this.$validate.isDateEqual(dateInDay, endDateUpper) ||
                this.$validate.isDateLess(dateInDay, endDateUpper))
            ) {
              const sameRequest = this._checkSameRequest(payload, cacheFilter[upper].payload)
              if (sameRequest) {
                const date = this.$formatter.convertDate(foundDateInDay.value)
                list = cacheFilter[upper].data.filter(el => {
                  const d = this.$formatter.convertDate(el.date)
                  return this.$validate.isDateEqual(date, d)
                })
              }
            }
          }
        }
      }

      return list
    },
    async _getLessonCourseCategory() {
      try {
        const ids = []
        for (let i = 0; i < this.timetableData.length; i++) {
          if (
            this.timetableData[i].course_category_id > -1 &&
            !ids.includes(this.timetableData[i].course_category_id)
          ) {
            ids.push(this.timetableData[i].course_category_id)
          }
        }
        if (ids.length) {
          const data = await this.$Fetcher.GetCourseCategoriesByIdList(ids)
          for (let i = 0; i < this.timetableData.length; i++) {
            if (this.timetableData[i].course_category_id > -1) {
              const foundCate = data.find(el => el.id === this.timetableData[i].course_category_id)
              if (foundCate) {
                if(this.timetableData[i].course_name_tmp == undefined) {
                  this.timetableData[i].course_name_tmp = this.timetableData[i].course_name
                }
                this.timetableData[i].course_name = this.timetableData[i].course_name_tmp+` (${this.$formatter.generateCourseTitle(
                  { grade: this.timetableData[i].grade, category_data: { name: foundCate.name } },
                  this.$store.getters.gradeOptions,
                )})`
              }
            }
          }
        }
      } catch (err) {
        this.$common.error(err)
      }
    },
    async _getPackageSort(lessons = []) {
      if (this.isAppMode(['T01'])) return

      if (lessons.length > 0) {
        try {
          const lessonIds = lessons.slice(0, 10)
          const data = await this.$Fetcher.GetPackageSort(lessonIds, 'get_package_sort')

          for (let i = 0; i < this.timetableData.length; i++) {
            const foundLesson = data.find(el => el.id === this.timetableData[i].id)
            if (foundLesson) {
              for (let x = 0; x < this.timetableData[i].student_data.length; x++) {
                const foundStudent = foundLesson.attendance_list.find(
                  el => el.student_id === this.timetableData[i].student_data[x].id,
                )

                if (foundStudent) {
                  this.timetableData[i].student_data[x].package_quota_count = foundStudent.package_quota_count
                }
              }
            }
          }

          lessons = lessons.splice(10)
          if (lessons.length) {
            await this._getPackageSort(lessons)
          }
        } catch {}
      }
    },
    _clearFilter() {
      this.filter.course = []
      this.filter.tutor = []
      this.filter.date = ''
    },
    onSearchDateChange(val) {
      if (this.tableLoading) return

      if (!this.$validate.DataValid(val)) {
        this.$refs.timetable.setCurrentValue()
        this.$refs.timetable.setCalendarType(this.defaultCalendarType)
        this.filter.calendarType = this.defaultCalendarType
      } else {
        this.$refs.timetable.setCurrentValue(val)
        this.$refs.timetable.setCalendarType('day')
        this.filter.calendarType = 'day'
      }

      const dateList = this.$refs.timetable.getDatesList()
      this.$refs.timetable.setDateList(dateList)
      this.getTimetable(dateList)
    },
    search() {
      if (this.tableLoading) return
      this.getTimetable()
    },
    clearSearch() {
      if (this.tableLoading) return
      this._clearFilter()
      this.onSearchDateChange()
    },
    refreshData() {
      if (this.tableLoading) return
      this.reloadClick = true
      const dateList = this.$refs.timetable.getDatesList()
      this.$refs.timetable.setDateList(dateList)
      this.getTimetable(dateList, false)
    },

    async _getQuery() {
      this._clearFilter()

      const query = this.gfGetTableQuery({
        date: 'date',
        start_date: 'date',
        type: ['week', 'month', 'day'],
        tutor: 'array_int',
        course: 'array_int',
        all: 'string',
      })
      if (this.$validate.DataValid(query['tutor'])) this.filter.tutor = query['tutor']
      if (this.$validate.DataValid(query['course'])) this.filter.course = query['course']
      if (this.$validate.DataValid(query['all'])) this.filter.allCenters = true
      if (this.$validate.DataValid(query['date'])) this.filter.date = query['date']

      if (this.$validate.DataValid(query['type'])) {
        this.$refs.timetable.setCalendarType(query['type'])
        this.filter.calendarType = query['type']
      } else {
        this.$refs.timetable.setCalendarType(this.defaultCalendarType)
      }

      if (this.$validate.DataValid(query['start_date'])) {
        this.$refs.timetable.setCurrentValue(query['start_date'])
      } else {
        this.$refs.timetable.setCurrentValue()
      }

      this.filter.dateList = this.$refs.timetable.getDatesList()
      this.$refs.timetable.setDateList(this.filter.dateList)
    },
    _regenerateURL() {
      const todayStr = this.$refs.timetable.getTodayStr()
      const q = {
        date: this.filter.date,
        start_date: this.filter.dateList[0] && this.filter.dateList[0] !== todayStr ? this.filter.dateList[0] : '',
        type: this.filter.calendarType !== this.defaultCalendarType ? this.filter.calendarType : '',
        course: this.filter.course.length ? this.filter.course.join(',') : undefined,
        tutor: this.filter.tutor.length ? this.filter.tutor.join(',') : undefined,
        all: this.filter.allCenters ? true : undefined,
      }

      const newQuery = this.$common.diffQuery(q, this.$route.query)
      if (newQuery) this.$router.replace({ query: newQuery })
    },

    async _getSetting() {
      let data = null
      if (this.gEnableCustomGrade) {
        try {
          data = await this.$Fetcher.GetSetting()
        } catch (err) {
          this.$common.error(err)
        }
      }
      this.disableCourseGrade = this.gfCheckDisabledCourseGrade(data)
    },
    async _getTutorOptions() {
      try {
        let payload = {}
        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) payload['center'] = [selectedSchool]

        const { data } = await this.$Fetcher.GetStaffs(payload)
        this.tutorOptions = data.map(el => {
          return { value: el.id, text: el.name }
        })
      } catch (err) {
        if (err === 'abort') {
          return false
        }
      }
      return true
    },
    async _getCourseOptions() {
      try {
        let payload = {
          join_category_data: true,
        }
        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) payload['center_id'] = selectedSchool

        const { data } = await this.$Fetcher.GetCourses(payload)
        this.courseOptions = await this.gfSortCourseOptions(data)
      } catch (err) {
        if (err === 'abort') {
          return false
        }
      }
      return true
    },
    async _getHolidays() {
      if (this.$validate.DataValid(this.$excludeMenuList) && this.$excludeMenuList.includes('setting-holiday')) {
        return true
      }

      try {
        const selectedSchool = this.gfGetUserSelectedSchool()
        this.holidayList = await this.$Fetcher.GetHolidaysWithCenter(selectedSchool)
      } catch (err) {
        if (err === 'abort') {
          return false
        }
      }

      return true
    },

    navLessonDetail(id) {
      if (this.$validate.allowAction('ClassDetails', this.userType)) {
        // staff只可以檢視所屬分校lesson
        const foundLesson = this.timetableData.find(el => el.id === id)
        const getUserSchools = this.gfGetUserSchools()
        if (this.$validate.DataValid(foundLesson.school_id) && !getUserSchools.includes(foundLesson.school_id)) {
          this.$store.dispatch('toggleSnack', { type: 'error', message: 'message.notBelongLessonBranch' })
          return
        }

        this.$router.push({ name: 'ClassDetails', params: { id: id } })
      }
    },

    // ===== 匯出報表 ====
    async exportData() {
      if (this.exportLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      this.exportLoading = true

      // check loaded

      try {
        let header = {
          date: this.$t('lessonDate'),
          weekday: this.$t('weekday'),
          course_name: this.$t('courseName'),
          start_time: this.$t('courseStartTime'),
          end_time: this.$t('courseEndTime'),
          grade: this.$t('grade'),
          tutor: this.$t('tutor'),
          classroom: this.$t('classroom'),
          student_num: this.$t('personCount'),
          students: this.$t('student'),
          price: this.$t('price'),
        }
        if (!this.$validate.allowAction('viewSalary', this.userType) || this.isAppMode(['T02'])) {
          delete header['price']
        }
        if (!this.isAppMode(['T49'])) {
          delete header['weekday']
        }
        const payload = await this._getPayload()
        const { data } = await this.$Fetcher.GetLessons(payload)
        let filteredData = data
        // staff只可以攞所屬分校lesson
        const getUserSchools = this.gfGetUserSchools()
        filteredData = data.filter(
          el => !this.$validate.DataValid(el.school_id) || getUserSchools.includes(el.school_id),
        )

        const exportData = filteredData.map(el => {
          const studentData = []
          for (let i = 0; i < el.attendance_list.length; i++) {
            const student = el.attendance_list[i]
            if (
              this.$validate.DataValid(student) &&
              !student.removed_student &&
              (student.status === 'pending' ||
                student.status === 'attend' ||
                student.status === 'absent' ||
                student.status === 'sick_leave' ||
                student.status === 'await')
            ) {
              const studentGrade = this.gradeOptions.find(item => item.value === student.grade)
              studentData.push(
                `${student.student_name}${student.student_code ? ` (${student.student_code})` : ''}${
                  this.isAppMode(['T46']) && studentGrade ? ` (${studentGrade.text})` : ''
                }`,
              )
            }
          }

          const foundGrade = this.gradeOptions.find(item => item.value === el.grade)
          return {
            date: el.date,
            weekday: this.$d(this.$formatter.convertDate(el.date), 'weekdayShort'),
            course_name: el.course_name,
            start_time: el.start_time,
            end_time: el.end_time,
            grade: foundGrade ? foundGrade.text : el.grade,
            tutor: el.tutor_data_list.map(t => t.name).join(', '),
            classroom: el.classroom_data ? el.classroom_data.name : '',
            student_num: `${el.current_student_num} / ${el.max_student === -1 ? this.$t('noLimit') : el.max_student}`,
            price: el.price,
            students: studentData.join(',\n'),
          }
        })

        const todayStr = this.$refs.timetable.getTodayStr()
        this.exportExcel(header, exportData, this.$t('menu.timetable'), `timetable_${todayStr.replace(/-/g, '')}.xlsx`)
      } catch (err) {
        this.$common.error(err)
        if (err !== 'abort') {
          this.$store.dispatch('toggleSnack', {
            message: 'message.exportFail',
            type: 'error',
          })
        }
      } finally {
        this.exportLoading = false
      }
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: Timetable')
      await this._getSetting()
      this.userType = this.gfGetUserType()
      this._getQuery()
      this.$store.dispatch('toggleLoadingPage', false)

      let next = await this.getTimetable(this.filter.dateList, false)
      if (next) next = await this._getHolidays()
      if (next) next = await this._getTutorOptions()
      if (next) next = await this._getCourseOptions()
    },
  },

  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.tableLoading = true
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },

  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    this.$XHR.abort()
    next()
  },
}
</script>

<style lang="scss" scoped>
::v-deep.v-input--switch {
  & .v-input--switch__track {
    color: #ffffff !important;
    opacity: 1 !important;
  }

  & .v-label {
    color: #ffffff !important;
    font-size: 12px !important;
    font-weight: 500 !important;
  }
}
</style>
